import React from 'react';

export interface CrmConfig {
  name?: string;
  theme?: string;
  maxRows?: number;
  hotCallWindow?: number;
  enableChatClient?: boolean;
  enableCurrentCallers?: boolean;
  enableVoicemails?: boolean;
  voicemailsRequirePin?: boolean;
  enableCdr?: boolean;
  hasCdrPassword?: boolean;
  pinFlagPasswordHash?: string;
  printPasswordHash?: string;
  adminPasswordHash?: string;
  freepbxServerId?: string;
  chatDids?: string;
  defaultChatDid?: string;
  chatFolders?: string;
  nameUrlFormat?: string;
  phoneUrlFormat?: string;
  nameUrlTemplate?: string;
  nameUrlTarget?: string;
  numberUrlTemplate?: string;
  phoneUrlTarget?: string;
  displayFormat?: string;
  idleLogoutTime?: number;
  [key: string]: any;
}

export interface CrmLog {
  id: string;
  sourceIp?: string;
  targetNumber: string;
  responsePayload?: string;
  resolvedStatus?: string;
  createdAt: number;
  [key: string]: any;
}

export interface ActiveAgent {
  name: string;
  lastActive: number;
  status?: string;
}

export interface CrmDashboardData {
  crm: CrmConfig;
  endpointName?: string;
  logs: CrmLog[];
  serverConfig?: any;
  [key: string]: any;
}

export function parseDate(ts: number | string, clientTimezone?: string) {
  try {
    const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit' };
    if (clientTimezone && clientTimezone !== 'local') {
      options.timeZone = clientTimezone;
    }
    const d = new Date(Number(ts) < 10000000000 ? Number(ts) * 1000 : Number(ts));
    const timeStr = d.toLocaleTimeString([], options);

    const today = new Date();
    if (d.getDate() !== today.getDate() || d.getMonth() !== today.getMonth() || d.getFullYear() !== today.getFullYear()) {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return { timeStr, dateStr: `${mm}/${dd}/${yyyy}` };
    }
    return { timeStr, dateStr: null };
  } catch {
    return {
      timeStr: new Date(Number(ts) < 10000000000 ? Number(ts) * 1000 : Number(ts)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      dateStr: null
    };
  }
}

export function getUrl(template: string, formattedName: string, formattedNum: string) {
  if (!template) return '#';
  return template.replace(/\[name\]/g, encodeURIComponent(formattedName)).replace(/\[number\]/g, encodeURIComponent(formattedNum));
}

export function formatPhoneUrl(phone: string, style: string) {
  let digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);

  if (style === 'e164') return `+1${digits}`;
  if (style === 'eleven_digit') return `1${digits}`;
  if (style === 'ten_digit') return digits;
  if (style === 'dashed') return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone;
}

export function formatNameUrl(name: string, style: string) {
  if (!name || name === '-' || name === 'UNKNOWN') return '';
  let first = '';
  let last = '';
  const rawName = name.trim();
  if (rawName.includes(',')) {
    const commaIdx = rawName.indexOf(',');
    last = rawName.slice(0, commaIdx).replace(/[^a-zA-Z\s]/g, ' ').replace(/\s+/g, ' ').trim();
    first = rawName.slice(commaIdx + 1).replace(/[^a-zA-Z\s]/g, ' ').replace(/\s+/g, ' ').trim();
  } else {
    const dictName = rawName.replace(/[^a-zA-Z\s]/g, ' ').replace(/\s+/g, ' ').trim();
    const parts = dictName.split(' ');
    first = parts.length > 0 ? parts[0] : '';
    last = parts.length > 1 ? parts.slice(1).join(' ') : '';
  }

  if (style === 'raw') return name;
  if (style === 'first_last') return `${first} ${last}`.trim();
  if (style === 'last_first') return `${last} ${first}`.trim();
  if (style === 'lastfirst') return `${last}${first}`;
  if (style === 'firstlast') return `${first}${last}`;
  if (style === 'last_only') return last || first;
  return name;
}

export function formatDisplayHtml(
  name: string,
  phone: string,
  crm: any,
  urlName: string,
  urlPhone: string,
  timestamp: number,
  isLight: boolean,
  hotCallWindow: number | null | undefined,
  disableLinks: boolean = false,
  disableEditIcon: boolean = false,
  hidePhoneIfName: boolean = false,
  onEditContact?: (phone: string, name: string) => void
) {
  const isHotCall = hotCallWindow ? (Math.floor(Date.now() / 1000) - Number(timestamp)) <= (hotCallWindow * 60) : false;

  const safePhone = phone || '';
  let digits = safePhone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  const dashedPhone = digits.length >= 10 ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}` : safePhone;

  let first = '';
  let last = '';
  const rawName = (name || '').trim();
  if (rawName.includes(',')) {
    const commaIdx = rawName.indexOf(',');
    last = rawName.slice(0, commaIdx).replace(/[^a-zA-Z0-9\s\-\.]/g, ' ').replace(/\s+/g, ' ').trim();
    first = rawName.slice(commaIdx + 1).replace(/[^a-zA-Z0-9\s\-\.]/g, ' ').replace(/\s+/g, ' ').trim();
  } else {
    const dictName = rawName.replace(/[^a-zA-Z0-9\s\-\.]/g, ' ').replace(/\s+/g, ' ').trim();
    const parts = dictName.split(' ');
    first = parts.length > 0 ? parts[0] : '';
    last = parts.length > 1 ? parts.slice(1).join(' ') : '';
  }

  let namePart = rawName || '-';
  if (namePart !== '-' && (first || last)) {
    if (crm?.displayFormat === 'first_last_brackets') namePart = `${first} ${last}`.trim();
    else if (crm?.displayFormat === 'last_first_brackets') namePart = last ? `${last}, ${first}`.trim() : first;
  }

  const hasValidName = Boolean(namePart && namePart !== '-' && namePart.toUpperCase() !== 'UNKNOWN' && namePart.toUpperCase() !== 'UNAVAILABLE');

  const FlameIcon = () => (
    <svg style={{ display: 'inline', marginRight: '6px', verticalAlign: 'text-bottom', filter: 'drop-shadow(0 0 4px rgba(249, 115, 22, 0.4))' }} width="17" height="17" viewBox="0 0 24 24" fill="rgba(249, 115, 22, 0.2)" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path></svg>
  );

  const displayNameText = hasValidName ? namePart : dashedPhone;

  const renderName = crm?.nameUrlTemplate && !disableLinks ? (
    <a href={getUrl(crm.nameUrlTemplate, urlName, urlPhone)} target={crm.nameUrlTarget || '_blank'} rel="noreferrer" style={{ fontWeight: isHotCall ? 800 : 600, color: isLight ? '#09090b' : 'white', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flexShrink: 1, display: 'block' }} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
      {isHotCall && <FlameIcon />}
      {displayNameText}
    </a>
  ) : (
    <span style={{ fontWeight: isHotCall ? 800 : 600, color: isLight ? '#09090b' : 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flexShrink: 1, display: 'block' }}>
      {isHotCall && <FlameIcon />}
      {displayNameText}
    </span>
  );

  const shouldRenderPhone = !hidePhoneIfName && hasValidName;

  const renderPhone = shouldRenderPhone ? (
    crm?.numberUrlTemplate && !disableLinks ? (
      <a href={getUrl(crm.numberUrlTemplate, urlName, urlPhone)} target={crm.phoneUrlTarget || '_blank'} rel="noreferrer" style={{ color: isLight ? '#0ea5e9' : '#38bdf8', opacity: 0.9, textDecoration: 'none', flexShrink: 0, whiteSpace: 'nowrap' }} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
        ({dashedPhone})
      </a>
    ) : <span style={{ color: isLight ? '#0ea5e9' : '#38bdf8', opacity: 0.9, flexShrink: 0, whiteSpace: 'nowrap' }}>({dashedPhone})</span>
  ) : null;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      {crm?.freepbxServerId && !disableEditIcon && onEditContact && (
        <span onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          onEditContact(phone, hasValidName ? namePart : '');
        }} title="Edit Contact" style={{ cursor: 'pointer', display: 'inline-flex', opacity: 0.5, transition: 'opacity 0.2s' }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
        </span>
      )}
      {renderName}
      {renderPhone}
    </span>
  );
}
